import React, { useEffect, useState } from "react";
import { CiFilter } from "react-icons/ci";
import SearchIcon from "@mui/icons-material/Search";
import Pagination from "@mui/material/Pagination";
import DeviceEntryForm from "./Device Entry/DeviceEntryForm";
import ViewDeviceModal from "./ViewDevice";
import DeviceFilterModal from "./Device Filter/DeviceFilterModal";
import API_BASE_URL from "../../../../Constants";

import { useAuth } from "../../../../contexts/AuthContext";
import RowsPerPageSelector from "../../../../components/Tables/Pagination/RowsPerPageSelector";
import { deviceListFields } from "./config/deviceConfig";

const DevicesTable = () => {
  const { currentUser } = useAuth();
  const [devices, setDevices] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState("table");

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const rowsPerPage = currentUser?.rowsPerPage ?? 25;

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const sorted = [...devices].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";

      // Handle "" values explicitly
      if (aValue === "" && bValue !== "") {
        return sortConfig.direction === "ascending" ? 1 : -1; // a comes last in ascending, first in descending
      }
      if (bValue === "" && aValue !== "") {
        return sortConfig.direction === "ascending" ? -1 : 1; // b comes last in ascending, first in descending
      }
      if (aValue === "" && bValue === "") {
        return 0; // both are "", equal
      }

      const aIsNumber = !isNaN(Number(aValue));
      const bIsNumber = !isNaN(Number(bValue));

      if (aIsNumber && bIsNumber) {
        // Both are numbers, compare numerically
        return sortConfig.direction === "ascending"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      } else if (aIsNumber && !bIsNumber) {
        // a is a number, b is a string
        return sortConfig.direction === "ascending" ? -1 : 1;
      } else if (!aIsNumber && bIsNumber) {
        // a is a string, b is a number
        return sortConfig.direction === "ascending" ? 1 : -1;
      } else {
        // Both are strings, compare lexicographically
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      }
    });
    setDevices(sorted);
  }, [sortConfig]);

  const fetchDevices = async (
    fetchPage = page,
    fetchSize = rowsPerPage,
    fetchSearch = searchQuery
  ) => {
    try {
      const queryString = fetchSearch
        ? `&q=${encodeURIComponent(fetchSearch)}`
        : "";
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings/devices?page=${
          fetchPage - 1
        }&size=${fetchSize}${queryString}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        console.log(data);
        setDevices(data.content);
        setTotalPages(data.totalPages);
      } else {
        console.error("API error:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [buildingsData, customersData, managementCompaniesData] =
          await Promise.all([
            fetch(`${API_BASE_URL}/api/setup/buildings/all`, {
              credentials: "include",
            }).then((res) => res.json()),
            fetch(`${API_BASE_URL}/api/setup/customers/all`, {
              credentials: "include",
            }).then((res) => res.json()),
            fetch(`${API_BASE_URL}/api/setup/managementcompanies/all`, {
              credentials: "include",
            }).then((res) => res.json()),
          ]);
        // Set state with fetched data
        setBuildings(buildingsData);
        setCustomers(customersData);
        setManagementCompanies(managementCompaniesData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    fetchDevices(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage]);

  const handleOpenEntryModal = () => setIsEntryModalOpen(true);
  const handleCloseEntryModal = () => setIsEntryModalOpen(false);
  const handleOpenViewModal = (deviceId) => {
    setSelectedDeviceId(deviceId);
    setIsViewModalOpen(true);
  };
  const handleCloseViewModal = () => setIsViewModalOpen(false);
  const handleOpenFilterModal = () => setIsFilterModalOpen(true);
  const handleCloseFilterModal = () => setIsFilterModalOpen(false);

  const setNext = () => {
    const currentIndex = devices.findIndex(
      (device) => device.deviceId === selectedDeviceId
    );
    if (currentIndex !== -1) {
      const nextIndex = (currentIndex + 1) % devices.length;
      setSelectedDeviceId(devices[nextIndex].deviceId);
    }
  };

  const setPrev = () => {
    const currentIndex = devices.findIndex(
      (device) => device.deviceId === selectedDeviceId
    );
    if (currentIndex !== -1) {
      const prevIndex = (currentIndex - 1 + devices.length) % devices.length;
      setSelectedDeviceId(devices[prevIndex].deviceId);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchDevices(page, rowsPerPage, searchQuery);
  };

  return (
    <div className="relative min-h-screen flex flex-col">
      <div className="flex justify-between items-center m-4 p-4">
        <h2 className="text-2xl font-bold">Device List</h2>
        <div className="flex justify-end gap-4">
          <form
            onSubmit={handleSubmit}
            className="flex items-center border border-gray-300 rounded-lg overflow-hidden"
          >
            <button
              type="submit"
              className="px-3 py-2 text-gray-500 cursor-pointer border-r border-gray-300 bg-gray-50 hover:bg-gray-100 hover:text-sss-blue duration-500"
            >
              <SearchIcon />
            </button>
            <input
              type="text"
              className="px-3 py-2 w-full outline-none"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </form>
          <button
            className="py-2 px-3 border-sss-blue-sel rounded-lg flex border text-sss-blue-sel font-bold hover:bg-sss-blue hover:text-white transition duration-300 ease-in-out"
            onClick={handleOpenFilterModal}
          >
            <CiFilter className="text-2xl" /> Filter
          </button>
          <button
            className="border border-sss-accent-yel bg-sss-accent-yel rounded-lg px-5 py-2 flex text-sss-blue-sel font-bold hover:bg-yellow-500 transition duration-300 ease-in-out"
            onClick={handleOpenEntryModal}
          >
            Add +
          </button>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="px-2 py-2 border-2 border-sss-blue rounded-lg focus:border-sss-blue text-sss-blue-sel font-bold focus:outline-none"
          >
            <option value="table">Table View</option>
            <option value="card">Card View</option>
          </select>
        </div>
      </div>
      {viewMode === "table" ? (
        <div className="mx-auto pl-4 pr-4 h-screen w-full">
          <div className="overflow-y-auto max-h-[calc(100vh-200px)] border-2 border-gray-200 rounded-lg shadow-lg">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-200 sticky top-0 shadow-md z-10">
                <tr>
                  <th className="px-4 py-2 text-left font-bold text-sss-blue-sel"></th>
                  {deviceListFields.map((field) => (
                    <th
                      key={field.key}
                      className="px-4 py-2 text-left font-bold text-sss-blue-sel cursor-pointer"
                      onClick={() => handleSort(field.key)}
                    >
                      {field.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {devices.map((device) => (
                  <tr key={device.deviceId} className="border-t">
                    <td className="px-4 py-2">
                      <button
                        className="text-sss-info"
                        onClick={() => handleOpenViewModal(device.deviceId)}
                      >
                        View
                      </button>
                    </td>
                    {deviceListFields.map((field) => (
                      <td key={field.key} className="px-4 py-2">
                        {field.map
                          ? field.map[device[field.key]] || "N/A"
                          : device[field.key] || "N/A"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="m-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {devices.map((device) => (
            <div
              key={device.deviceId}
              className="bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200 flex"
            >
              {/* Left half: Image */}
              <div className="w-1/2 flex items-center justify-center">
                <img
                  src="/favicon.ico"
                  alt="Profile"
                  className="w-48 h-48 rounded-full object-cover"
                />
              </div>
              <div className="flex w-1/2 p-4">
                <div className="flex-1">
                  <p className="font-bold text-lg">Device: {device.deviceId}</p>
                  <button
                    className="text-sss-info mr-2"
                    onClick={() => handleOpenViewModal(device.deviceId)}
                  >
                    View
                  </button>
                  {deviceListFields.map((field) => (
                    <p key={field.key} className="text-sm text-gray-600">
                      {field.label}:{" "}
                      {field.map
                        ? field.map[device[field.key]] || "-"
                        : device[field.key] || "-"}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 px-4 py-2 border-t border-gray-300 fixed bottom-0 left-0 w-full bg-white">
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          shape="rounded"
        />
        <div className="flex items-center space-x-2">
          <RowsPerPageSelector />
        </div>
      </div>

      {isViewModalOpen && (
        <ViewDeviceModal
          open={isViewModalOpen}
          id={selectedDeviceId} // Ensure this is correct
          refreshDevices={() => fetchDevices(page, pageSize, searchQuery)}
          onClose={handleCloseViewModal}
          setNext={setNext}
          setPrev={setPrev}
          buildings={buildings}
          customers={customers}
          managementCompanies={managementCompanies}
        />
      )}

      <DeviceEntryForm
        open={isEntryModalOpen}
        onClose={handleCloseEntryModal}
        refreshDevices={() => fetchDevices(page, pageSize, searchQuery)}
        buildings={buildings}
        customers={customers}
        managementCompanies={managementCompanies}
      />
      <DeviceFilterModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
      />
    </div>
  );
};

export default DevicesTable;
